import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import AnimatedDemo from './AnimatedDemo';
import './HowItWorksPage.css';

const STEPS = [
  {
    number: '01',
    title: 'Input your content',
    description: 'Paste text, enter a website URL, or upload a document (PDF, Word). Mindmark accepts any format your practice uses.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>
    )
  },
  {
    number: '02',
    title: 'AI analysis',
    description: 'Your content is checked against AHPRA advertising guidelines, the Psychology Board Code of Conduct, and mandatory notification requirements.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    )
  },
  {
    number: '03',
    title: 'Get your results',
    description: 'Receive a detailed report with flagged issues ranked by severity, the specific regulation violated, and plain-language suggestions for compliant alternatives.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
        <path d="M9 11l3 3L22 4" />
        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
      </svg>
    )
  }
];

const MOCK_REPORT = {
  inputText: '"We guarantee significant improvement in your mental health within 6 sessions."',
  issues: [
    {
      risk: 'high',
      rule: 'UNREASONABLE EXPECTATION (S.133(1)(D))',
      problematicText: 'We guarantee significant improvement in your mental health within 6 sessions.',
      explanation: 'This statement creates an unreasonable expectation of beneficial treatment by guaranteeing a specific outcome within a defined timeframe. Under Section 133(1)(d) of the National Law, advertising must not create unreasonable expectations of beneficial treatment.',
      suggestion: 'We aim to support your mental health through evidence-based treatment approaches. Individual experiences may vary.'
    }
  ]
};

const REGULATIONS = [
  {
    name: 'AHPRA Advertising Guidelines',
    subtitle: 'Section 133, National Law',
    note: null,
    checks: [
      'Testimonials using clinical aspects (symptoms, diagnosis, outcomes)',
      'Unreasonable expectations or guaranteed outcomes',
      'Specialist title misuse without registration',
      'Gifts or discounts without stated terms and conditions'
    ]
  },
  {
    name: 'Psychology Board Code of Conduct',
    subtitle: 'Effective December 2025',
    note: 'Updated December 2025 \u2014 replaced the APS Code of Ethics',
    checks: [
      'Informed consent requirements before services',
      'Advertising accuracy and professional representation',
      'Confidentiality and privacy obligations',
      'Professional boundaries in communications'
    ]
  },
  {
    name: 'Mandatory Notifications Guidelines',
    subtitle: 'AHPRA, March 2020',
    note: null,
    checks: [
      'Notifiable conduct awareness (intoxication, misconduct)',
      'Impairment placing public at risk of harm',
      'Significant departure from professional standards',
      'Self-reporting requirements (Section 130)'
    ]
  }
];

function HowItWorksPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.dataset.section]));
          }
        });
      },
      { threshold: 0.15 }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const handleGetStarted = () => {
    if (user) {
      navigate('/checker');
    } else {
      navigate('/signup');
    }
  };

  return (
    <div className="how-it-works-page">
      <Navbar user={user} onLogout={onLogout} />

      {/* Hero */}
      <section className="hiw-hero">
        <div className="hiw-hero-container">
          <h1>How Mindmark Works</h1>
          <p>
            Check your psychology practice content against Australian
            regulations in three simple steps.
          </p>
        </div>
      </section>

      {/* 3-Step Process */}
      <section
        className="hiw-steps"
        ref={(el) => (sectionRefs.current.steps = el)}
        data-section="steps"
      >
        <div className="hiw-steps-container">
          <div className="hiw-steps-grid">
            {STEPS.map((step, index) => (
              <div
                key={index}
                className={`hiw-step-card ${visibleSections.has('steps') ? 'visible' : ''}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="hiw-step-number">{step.number}</div>
                <div className="hiw-step-icon">{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Animated Walkthrough */}
      <section className="hiw-demo-section">
        <div className="hiw-demo-container">
          <h2>See it in action</h2>
          <p className="hiw-demo-subtitle">
            Watch Mindmark analyse sample text and flag potential
            compliance issues in real time.
          </p>
          <div className="hiw-demo-wrapper">
            <AnimatedDemo />
          </div>
        </div>
      </section>

      {/* Mock Compliance Report */}
      <section
        className="hiw-report-section"
        ref={(el) => (sectionRefs.current.report = el)}
        data-section="report"
      >
        <div className="hiw-report-container">
          <h2>What your report looks like</h2>
          <p className="hiw-report-subtitle">
            Each flagged issue includes the severity, regulation
            reference, and a suggested compliant alternative.
          </p>

          <div className={`hiw-mock-report ${visibleSections.has('report') ? 'visible' : ''}`}>
            <div className="hiw-mock-input">
              <span className="hiw-mock-label">Checked text:</span>
              <span className="hiw-mock-text">{MOCK_REPORT.inputText}</span>
            </div>

            {MOCK_REPORT.issues.map((issue, index) => (
              <div key={index} className={`hiw-issue-card risk-${issue.risk}`}>
                <div className="hiw-issue-header">
                  <span className={`hiw-risk-badge ${issue.risk}`}>
                    {issue.risk === 'high' ? 'High Risk' :
                     issue.risk === 'medium' ? 'Medium Risk' : 'Low Risk'}
                  </span>
                  <span className="hiw-issue-rule">{issue.rule}</span>
                </div>
                <p className="hiw-issue-text">
                  <strong>Problem:</strong> &ldquo;{issue.problematicText}&rdquo;
                </p>
                <p className="hiw-issue-explanation">{issue.explanation}</p>
                <div className="hiw-issue-suggestion">
                  <strong>Suggestion:</strong> {issue.suggestion}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regulations We Check */}
      <section
        className="hiw-regulations-section"
        ref={(el) => (sectionRefs.current.regulations = el)}
        data-section="regulations"
      >
        <div className="hiw-regulations-container">
          <h2>Regulations we check against</h2>
          <p className="hiw-regulations-subtitle">
            Mindmark analyses your content against the key Australian
            regulations governing psychology practice advertising.
          </p>

          <div className="hiw-regulations-grid">
            {REGULATIONS.map((reg, index) => (
              <div
                key={index}
                className={`hiw-regulation-card ${visibleSections.has('regulations') ? 'visible' : ''}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <h3>{reg.name}</h3>
                <span className="hiw-regulation-subtitle">{reg.subtitle}</span>
                {reg.note && <span className="hiw-regulation-note">{reg.note}</span>}
                <ul>
                  {reg.checks.map((check, i) => (
                    <li key={i}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {check}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="hiw-cta-section">
        <div className="hiw-cta-container">
          <h2>Ready to check your content?</h2>
          <p>Get started with 3 free checks every month.</p>
          <div className="hiw-cta-buttons">
            <button className="btn-primary btn-large" onClick={handleGetStarted}>
              {user ? 'Go to Checker' : 'Sign up for free'}
            </button>
            <Link to="/pricing" className="btn-secondary btn-large">
              View pricing
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default HowItWorksPage;
